import Data from 'models/DataModel';
import { SavedAnswer } from 'models/SavedAnswerModel';

export interface AnswerMergeConflict {
  kind: 'answer';
  questionId: string;
  baseValue: unknown;
  serverValue: unknown;
  localValue: unknown;
}

export interface TokenMergeConflict {
  kind: 'token';
  questionId: string;
  token: string;
  baseValue: unknown;
  serverValue: unknown;
  localValue: unknown;
}

export interface AddressMergeConflict {
  kind: 'address';
  baseValue: unknown;
  serverValue: unknown;
  localValue: unknown;
}

export interface AttachmentMergeConflict {
  kind: 'attachments';
  baseValue: unknown;
  serverValue: unknown;
  localValue: unknown;
}

export interface DataFieldMergeConflict {
  kind: 'data';
  field: 'BusinessObjectReference' | 'DispatchKey';
  baseValue: unknown;
  serverValue: unknown;
  localValue: unknown;
}

export type DataMergeConflict =
  | AnswerMergeConflict
  | TokenMergeConflict
  | AddressMergeConflict
  | AttachmentMergeConflict
  | DataFieldMergeConflict;

export interface OrderItemDataMergeResult {
  data: Data;
  conflicts: DataMergeConflict[];
}

export class OrderItemDataMergeConflictError extends Error {
  readonly status = 412;
  readonly conflicts: DataMergeConflict[];

  constructor(conflicts: DataMergeConflict[]) {
    super('Order item data merge has unresolved conflicts.');
    this.name = 'OrderItemDataMergeConflictError';
    this.conflicts = conflicts;
  }
}

interface FieldMergeResult<TValue> {
  value: TValue;
  hasConflict: boolean;
}

const hasOwn = (object: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(object, key);

const normalizeComparableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((normalized, key) => {
      normalized[key] = normalizeComparableValue((value as Record<string, unknown>)[key]);
      return normalized;
    }, {});
};

const areEqual = (left: unknown, right: unknown): boolean =>
  JSON.stringify(normalizeComparableValue(left)) === JSON.stringify(normalizeComparableValue(right));

const cloneTokens = (tokens: SavedAnswer['Tokens'] | undefined): Record<string, unknown> => ({ ...(tokens ?? {}) });

const cloneData = (data: Data): Data => ({
  ...data,
  Address: { ...(data.Address ?? { FormattedAddress: '' }) },
  Answers: Object.entries(data.Answers ?? {}).reduce<Record<string, SavedAnswer>>((answers, [questionId, answer]) => {
    answers[questionId] = answer.Tokens
      ? {
          Value: answer.Value,
          Tokens: cloneTokens(answer.Tokens)
        }
      : {
          Value: answer.Value
        };

    return answers;
  }, {}),
  Attachments: [...(data.Attachments ?? [])]
});

const mergeField = <TValue>(baseValue: TValue, serverValue: TValue, localValue: TValue): FieldMergeResult<TValue> => {
  const localChanged = !areEqual(baseValue, localValue);
  const serverChanged = !areEqual(baseValue, serverValue);

  if (localChanged && serverChanged && !areEqual(localValue, serverValue)) {
    return {
      value: localValue,
      hasConflict: true
    };
  }

  if (localChanged) {
    return {
      value: localValue,
      hasConflict: false
    };
  }

  return {
    value: serverValue,
    hasConflict: false
  };
};

const getAnswerValue = (answers: Record<string, SavedAnswer> | undefined, questionId: string): string | undefined => {
  if (!answers || !hasOwn(answers, questionId)) {
    return undefined;
  }

  return answers[questionId].Value;
};

const getTokenValue = (answers: Record<string, SavedAnswer> | undefined, questionId: string, token: string): unknown | undefined => {
  const tokens = answers?.[questionId]?.Tokens;

  if (!tokens || !hasOwn(tokens, token)) {
    return undefined;
  }

  return tokens[token];
};

const addAnswerMerge = (
  mergedAnswers: Record<string, SavedAnswer>,
  questionId: string,
  value: string | undefined,
  tokens: Record<string, unknown>
) => {
  if (value === undefined && Object.keys(tokens).length === 0) {
    return;
  }

  mergedAnswers[questionId] =
    Object.keys(tokens).length > 0
      ? {
          Value: value ?? '',
          Tokens: tokens
        }
      : {
          Value: value ?? ''
        };
};

const mergeAnswers = (
  baseAnswers: Record<string, SavedAnswer>,
  serverAnswers: Record<string, SavedAnswer>,
  localAnswers: Record<string, SavedAnswer>
): { answers: Record<string, SavedAnswer>; conflicts: DataMergeConflict[] } => {
  const answers: Record<string, SavedAnswer> = {};
  const conflicts: DataMergeConflict[] = [];
  const questionIds = new Set([...Object.keys(baseAnswers), ...Object.keys(serverAnswers), ...Object.keys(localAnswers)]);

  questionIds.forEach((questionId) => {
    const answerMerge = mergeField(
      getAnswerValue(baseAnswers, questionId),
      getAnswerValue(serverAnswers, questionId),
      getAnswerValue(localAnswers, questionId)
    );

    if (answerMerge.hasConflict) {
      conflicts.push({
        kind: 'answer',
        questionId,
        baseValue: getAnswerValue(baseAnswers, questionId),
        serverValue: getAnswerValue(serverAnswers, questionId),
        localValue: getAnswerValue(localAnswers, questionId)
      });
    }

    const baseTokens = baseAnswers[questionId]?.Tokens ?? {};
    const serverTokens = serverAnswers[questionId]?.Tokens ?? {};
    const localTokens = localAnswers[questionId]?.Tokens ?? {};
    const tokenKeys = new Set([...Object.keys(baseTokens), ...Object.keys(serverTokens), ...Object.keys(localTokens)]);
    const tokens: Record<string, unknown> = {};

    tokenKeys.forEach((token) => {
      const tokenMerge = mergeField(
        getTokenValue(baseAnswers, questionId, token),
        getTokenValue(serverAnswers, questionId, token),
        getTokenValue(localAnswers, questionId, token)
      );

      if (tokenMerge.hasConflict) {
        conflicts.push({
          kind: 'token',
          questionId,
          token,
          baseValue: getTokenValue(baseAnswers, questionId, token),
          serverValue: getTokenValue(serverAnswers, questionId, token),
          localValue: getTokenValue(localAnswers, questionId, token)
        });
      }

      if (tokenMerge.value !== undefined) {
        tokens[token] = tokenMerge.value;
      }
    });

    addAnswerMerge(answers, questionId, answerMerge.value, tokens);
  });

  return { answers, conflicts };
};

export const mergeOrderItemData = (base: Data, server: Data, local: Data): OrderItemDataMergeResult => {
  const baseData = cloneData(base);
  const serverData = cloneData(server);
  const localData = cloneData(local);
  const conflicts: DataMergeConflict[] = [];

  const businessObjectReference = mergeField(
    baseData.BusinessObjectReference,
    serverData.BusinessObjectReference,
    localData.BusinessObjectReference
  );
  if (businessObjectReference.hasConflict) {
    conflicts.push({
      kind: 'data',
      field: 'BusinessObjectReference',
      baseValue: baseData.BusinessObjectReference,
      serverValue: serverData.BusinessObjectReference,
      localValue: localData.BusinessObjectReference
    });
  }

  const dispatchKey = mergeField(baseData.DispatchKey, serverData.DispatchKey, localData.DispatchKey);
  if (dispatchKey.hasConflict) {
    conflicts.push({
      kind: 'data',
      field: 'DispatchKey',
      baseValue: baseData.DispatchKey,
      serverValue: serverData.DispatchKey,
      localValue: localData.DispatchKey
    });
  }

  const address = mergeField(baseData.Address?.FormattedAddress, serverData.Address?.FormattedAddress, localData.Address?.FormattedAddress);
  if (address.hasConflict) {
    conflicts.push({
      kind: 'address',
      baseValue: baseData.Address?.FormattedAddress,
      serverValue: serverData.Address?.FormattedAddress,
      localValue: localData.Address?.FormattedAddress
    });
  }

  const attachments = mergeField(baseData.Attachments ?? [], serverData.Attachments ?? [], localData.Attachments ?? []);
  if (attachments.hasConflict) {
    conflicts.push({
      kind: 'attachments',
      baseValue: baseData.Attachments ?? [],
      serverValue: serverData.Attachments ?? [],
      localValue: localData.Attachments ?? []
    });
  }

  const answerMerge = mergeAnswers(baseData.Answers ?? {}, serverData.Answers ?? {}, localData.Answers ?? {});

  return {
    data: {
      ...serverData,
      BusinessObjectReference: businessObjectReference.value ?? '',
      DispatchKey: dispatchKey.value ?? '',
      Address: {
        ...serverData.Address,
        FormattedAddress: address.value ?? ''
      },
      Answers: answerMerge.answers,
      Attachments: [...(attachments.value ?? [])]
    },
    conflicts: [...conflicts, ...answerMerge.conflicts]
  };
};
